import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import OButton from '../../../UI/FormControls/OButton';
import useHttp from '../../../../hooks/use-http';
import { colors } from '../../../../../shared/colors';
import { icons } from '../../../../../shared/icons';
import { handleErrorMessage } from '../../../../../shared/api/handleErrorMessage';
import { urls } from './api/urls';
import {
  MAKS_FOTOGRAFIJA,
  MAKS_ZNAKOVA,
  generisiIzvestajProjektaPDF,
  pripremiIzvestajIzOdgovora,
  pripremiSliku,
} from './generisiIzvestajProjektaPDF';

const KASNJENJE_PREGLEDA = 600;

const danas = () => {
  const d = new Date();
  const mesec = String(d.getMonth() + 1).padStart(2, '0');
  const dan = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mesec}-${dan}`;
};

const samoDatum = (vrednost) => (vrednost ? String(vrednost).slice(0, 10) : '');
const brojIliNull = (vrednost) => {
  if (vrednost === '' || vrednost === null || vrednost === undefined) {
    return null;
  }
  const broj = Number(vrednost);
  return Number.isFinite(broj) ? broj : null;
};

const dataUrlUFajl = async (dataUrl, naziv) => {
  const odgovor = await fetch(dataUrl);
  const blob = await odgovor.blob();
  return new File([blob], naziv, { type: 'image/jpeg' });
};

// Polje prikazuje dd.MM.yyyy, a model čuva yyyy-MM-dd. Nedovršen unos ne menja model.
const DatumPolje = ({ labela, vrednost, onChange }) => {
  const [lokalno, setLokalno] = useState(vrednost ? dayjs(vrednost) : null);

  useEffect(() => {
    const trenutno = lokalno?.isValid() ? lokalno.format('YYYY-MM-DD') : '';
    if (vrednost && vrednost !== trenutno) setLokalno(dayjs(vrednost));
  }, [vrednost]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        format="DD.MM.YYYY"
        label={labela}
        value={lokalno}
        onChange={(nova) => {
          setLokalno(nova);
          if (!nova) {
            onChange('');
          } else if (nova.isValid() && nova.year() >= 2000) {
            onChange(nova.format('YYYY-MM-DD'));
          }
        }}
        slotProps={{ textField: { size: 'small', fullWidth: true } }}
      />
    </LocalizationProvider>
  );
};

const Sekcija = ({ naslov, children }) => (
  <Box
    sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, display: 'grid' }}
  >
    <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1.5 }}>
      {naslov}
    </Typography>
    <Box sx={{ display: 'grid', gap: 1.5 }}>{children}</Box>
  </Box>
);

const TekstNaJezicima = ({
  labela,
  kljuc,
  model,
  maks,
  izmeni,
  redovi = 4,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: 1.5,
    }}
  >
    {['Srb', 'Eng'].map((sufiks) => {
      const polje = `${kljuc}${sufiks}`;
      const vrednost = model[polje] ?? '';
      return (
        <TextField
          key={polje}
          fullWidth
          multiline
          minRows={redovi}
          size="small"
          label={`${labela} (${sufiks.toUpperCase()})`}
          value={vrednost}
          helperText={`${vrednost.length} / ${maks}`}
          inputProps={{ maxLength: maks }}
          onChange={(e) => izmeni(polje, e.target.value)}
        />
      );
    })}
  </Box>
);

const IzmenaIzvestajaProjektaDialog = ({
  open,
  idIzvestaja,
  idMasine,
  onClose,
  onSacuvano,
}) => {
  const { t } = useTranslation();
  const { getData } = useHttp('');
  const [model, setModel] = useState(null);
  const [obrisaneFotografije, setObrisaneFotografije] = useState([]);
  const [jezik, setJezik] = useState('SRB');
  const [pregledUrl, setPregledUrl] = useState(null);
  const [osvezava, setOsvezava] = useState(false);
  const [cuva, setCuva] = useState(false);
  const urlRef = useRef(null);
  const fileRef = useRef(null);
  const sledeciKljuc = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    let aktivan = true;

    setModel(null);
    setObrisaneFotografije([]);
    setPregledUrl(null);

    const ucitaj = async () => {
      const odgovor = await getData({
        url: idIzvestaja ? urls.VRATI_JEDAN : urls.VRATI_PREDLOG,
        method: 'POST',
        data: idIzvestaja ? { id: idIzvestaja } : { idMasine },
      });

      if (!aktivan) return;
      if (!odgovor || odgovor.isAxiosError) {
        toast.error(handleErrorMessage(odgovor));
        onClose();
        return;
      }

      const izvestaj = await pripremiIzvestajIzOdgovora(odgovor);
      if (!aktivan) return;

      setModel({
        ...izvestaj,
        id: idIzvestaja ?? null,
        idMasine,
        datumIzvestaja: idIzvestaja
          ? samoDatum(izvestaj.datumIzvestaja)
          : danas(),
        rok: samoDatum(izvestaj.rok),
        faze: izvestaj.faze.map((faza) => ({
          ...faza,
          datumOd: samoDatum(faza.datumOd),
          datumDo: samoDatum(faza.datumDo),
        })),
      });
    };

    ucitaj();
    return () => {
      aktivan = false;
    };
  }, [open, idIzvestaja, idMasine]);

  // Pregled se generiše istom funkcijom kao PDF koji vidi klijent
  useEffect(() => {
    if (!model) return undefined;
    let aktivan = true;
    setOsvezava(true);
    const tajmer = setTimeout(async () => {
      try {
        const { doc } = await generisiIzvestajProjektaPDF({
          izvestaj: model,
          jezik,
        });
        if (!aktivan) return;
        const url = URL.createObjectURL(doc.output('blob'));
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = url;
        setPregledUrl(url);
      } catch (error) {
        console.error(error);
      } finally {
        if (aktivan) setOsvezava(false);
      }
    }, KASNJENJE_PREGLEDA);
    return () => {
      aktivan = false;
      clearTimeout(tajmer);
    };
  }, [model, jezik]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  const izmeni = useCallback(
    (kljuc, vrednost) => setModel((m) => ({ ...m, [kljuc]: vrednost })),
    [],
  );

  const izmeniFazu = (kljuc, polje, vrednost) =>
    setModel((m) => ({
      ...m,
      faze: m.faze.map((faza) =>
        faza.kljuc === kljuc ? { ...faza, [polje]: vrednost } : faza,
      ),
    }));

  const izmeniFotografiju = (kljucReda, polje, vrednost) =>
    setModel((m) => ({
      ...m,
      fotografije: m.fotografije.map((foto) =>
        foto.kljucReda === kljucReda
          ? { ...foto, [polje]: vrednost, izmenjena: true }
          : foto,
      ),
    }));

  const dodajFotografije = async (e) => {
    const fajlovi = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!fajlovi.length) return;

    const slobodno = MAKS_FOTOGRAFIJA - model.fotografije.length;
    if (fajlovi.length > slobodno) {
      toast.error(t('projectReports.maxPhotos'));
    }

    const nove = (
      await Promise.all(
        fajlovi.slice(0, Math.max(slobodno, 0)).map(async (fajl) => {
          const url = URL.createObjectURL(fajl);
          const slika = await pripremiSliku(url);
          URL.revokeObjectURL(url);
          if (!slika) return null;
          sledeciKljuc.current += 1;
          return {
            kljucReda: `n${sledeciKljuc.current}`,
            naziv: fajl.name,
            opisSrb: '',
            opisEng: '',
            slika,
            nova: true,
          };
        }),
      )
    ).filter(Boolean);

    if (nove.length) {
      setModel((m) => ({ ...m, fotografije: [...m.fotografije, ...nove] }));
    }
  };

  const obrisiFotografiju = (foto) => {
    if (foto.id) {
      setObrisaneFotografije((curr) => [...curr, foto.id]);
    }
    setModel((m) => ({
      ...m,
      fotografije: m.fotografije.filter(
        (red) => red.kljucReda !== foto.kljucReda,
      ),
    }));
  };

  const posalji = async (url, data) => {
    const odgovor = await getData({ url, method: 'POST', data });
    if (odgovor?.isAxiosError) {
      throw new Error(handleErrorMessage(odgovor));
    }
    return odgovor;
  };

  const sacuvaj = async () => {
    if (!model.datumIzvestaja) {
      toast.error(t('projectReports.dateRequired'));
      return;
    }

    if (
      model.faze.some(
        (faza) => faza.datumOd && faza.datumDo && faza.datumOd > faza.datumDo,
      )
    ) {
      toast.error(t('projectReports.phaseDatesError'));
      return;
    }

    setCuva(true);
    let izvestajSacuvan = false;
    try {
      const rezultat = await posalji(urls.SACUVAJ, {
        id: model.id,
        idMasine: model.idMasine,
        datumIzvestaja: model.datumIzvestaja,
        nazivKupca: model.nazivKupca,
        ukupanProgres: brojIliNull(model.ukupanProgres),
        rok: model.rok || null,
        autor: model.autor,
        prethodneSrb: model.prethodneSrb,
        prethodneEng: model.prethodneEng,
        naredneSrb: model.naredneSrb,
        naredneEng: model.naredneEng,
        vaznoSrb: model.vaznoSrb,
        vaznoEng: model.vaznoEng,
        faze: model.faze.map((faza) => ({
          kljuc: faza.kljuc,
          redosled: faza.redosled,
          progres: brojIliNull(faza.progres),
          datumOd: faza.datumOd || null,
          datumDo: faza.datumDo || null,
        })),
      });

      izvestajSacuvan = true;

      // Prvo brisanje, da novo dodate fotografije ne pređu ograničenje
      await obrisaneFotografije.reduce(
        (prethodno, id) =>
          prethodno.then(() => posalji(urls.FOTOGRAFIJA_BRISANJE, { id })),
        Promise.resolve(),
      );

      await model.fotografije.reduce(
        (prethodno, foto) =>
          prethodno.then(async () => {
            if (foto.nova) {
              const forma = new FormData();
              forma.append('idIzvestaja', rezultat.id);
              forma.append('opisSrb', foto.opisSrb ?? '');
              forma.append('opisEng', foto.opisEng ?? '');
              forma.append(
                'fajl',
                await dataUrlUFajl(foto.slika.podaci, foto.naziv),
              );
              await posalji(urls.FOTOGRAFIJA_UNOS, forma);
            } else if (foto.izmenjena) {
              await posalji(urls.FOTOGRAFIJA_IZMENA, {
                id: foto.id,
                opisSrb: foto.opisSrb,
                opisEng: foto.opisEng,
              });
            }
          }),
        Promise.resolve(),
      );

      toast.success(t('projectReports.saved'));
      onSacuvano();
    } catch (error) {
      if (izvestajSacuvan) {
        // Izveštaj je već u bazi: ponovni klik bi napravio duplikat, pa se prozor zatvara
        toast.error(t('projectReports.photosError'));
        onSacuvano();
      } else {
        toast.error(error.message);
      }
    } finally {
      setCuva(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={cuva ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ sx: { width: '96vw', height: '94vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {idIzvestaja ? t('projectReports.edit') : t('projectReports.new')}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          overflow: { xs: 'auto', lg: 'hidden' },
        }}
      >
        {!model ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress sx={{ color: colors.ORANGE }} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                p: 2,
                alignContent: 'start',
                overflow: { lg: 'auto' },
              }}
            >
              <Sekcija naslov={t('projectReports.basicData')}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 1.5,
                  }}
                >
                  <DatumPolje
                    labela={`${t('projectReports.reportDate')} *`}
                    vrednost={model.datumIzvestaja}
                    onChange={(vrednost) => izmeni('datumIzvestaja', vrednost)}
                  />
                  <DatumPolje
                    labela={t('projectReports.deadline')}
                    vrednost={model.rok}
                    onChange={(vrednost) => izmeni('rok', vrednost)}
                  />
                  <TextField
                    size="small"
                    label={t('projectReports.customer')}
                    value={model.nazivKupca ?? ''}
                    inputProps={{ maxLength: 250 }}
                    onChange={(e) => izmeni('nazivKupca', e.target.value)}
                  />
                  <TextField
                    size="small"
                    label={t('projectReports.author')}
                    value={model.autor ?? ''}
                    inputProps={{ maxLength: 250 }}
                    onChange={(e) => izmeni('autor', e.target.value)}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={`${t('projectProgress.overallProgress')} (%)`}
                    value={model.ukupanProgres ?? ''}
                    onChange={(e) => izmeni('ukupanProgres', e.target.value)}
                  />
                </Box>
              </Sekcija>

              <Sekcija naslov={t('projectReports.phases')}>
                {model.faze.map((faza) => (
                  <Box
                    key={faza.kljuc}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      label={`${t(`projectPhases.${faza.kljuc}`)} (%)`}
                      value={faza.progres ?? ''}
                      onChange={(e) =>
                        izmeniFazu(faza.kljuc, 'progres', e.target.value)
                      }
                    />
                    <DatumPolje
                      labela={t('projectReports.phaseFrom')}
                      vrednost={faza.datumOd}
                      onChange={(vrednost) =>
                        izmeniFazu(faza.kljuc, 'datumOd', vrednost)
                      }
                    />
                    <DatumPolje
                      labela={t('projectReports.phaseTo')}
                      vrednost={faza.datumDo}
                      onChange={(vrednost) =>
                        izmeniFazu(faza.kljuc, 'datumDo', vrednost)
                      }
                    />
                  </Box>
                ))}
              </Sekcija>

              <Sekcija naslov={t('projectReports.texts')}>
                <TekstNaJezicima
                  labela={t('projectReports.lastWeeks')}
                  kljuc="prethodne"
                  model={model}
                  maks={MAKS_ZNAKOVA.prethodne}
                  izmeni={izmeni}
                />
                <TekstNaJezicima
                  labela={t('projectReports.nextWeeks')}
                  kljuc="naredne"
                  model={model}
                  maks={MAKS_ZNAKOVA.naredne}
                  izmeni={izmeni}
                />
                <TekstNaJezicima
                  labela={t('projectReports.important')}
                  kljuc="vazno"
                  model={model}
                  maks={MAKS_ZNAKOVA.vazno}
                  izmeni={izmeni}
                  redovi={2}
                />
              </Sekcija>

              <Sekcija
                naslov={`${t('projectReports.photos')} (${
                  model.fotografije.length
                } / ${MAKS_FOTOGRAFIJA})`}
              >
                {model.fotografije.map((foto) => (
                  <Box
                    key={foto.kljucReda}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: '90px 1fr 1fr auto',
                      },
                      gap: 1.5,
                      alignItems: 'center',
                    }}
                  >
                    {foto.slika ? (
                      <img
                        src={foto.slika.podaci}
                        alt=""
                        style={{
                          width: 90,
                          height: 60,
                          objectFit: 'cover',
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <Box />
                    )}
                    {['Srb', 'Eng'].map((sufiks) => (
                      <TextField
                        key={sufiks}
                        size="small"
                        label={`${t(
                          'projectReports.photoCaption',
                        )} (${sufiks.toUpperCase()})`}
                        value={foto[`opis${sufiks}`] ?? ''}
                        inputProps={{ maxLength: MAKS_ZNAKOVA.opisFotografije }}
                        onChange={(e) =>
                          izmeniFotografiju(
                            foto.kljucReda,
                            `opis${sufiks}`,
                            e.target.value,
                          )
                        }
                      />
                    ))}
                    <IconButton
                      size="small"
                      title={t('projectReports.delete')}
                      onClick={() => obrisiFotografiju(foto)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {model.fotografije.length < MAKS_FOTOGRAFIJA && (
                  <Box>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      multiple
                      hidden
                      onChange={dodajFotografije}
                    />
                    <OButton
                      text={t('projectReports.addPhotos')}
                      startIcon={
                        <AddPhotoAlternateOutlinedIcon
                          htmlColor={colors.ORANGE}
                        />
                      }
                      onClick={() => fileRef.current?.click()}
                      style={{
                        color: colors.ORANGE,
                        backgroundColor: colors.WHITE,
                        border: `1px solid ${colors.ORANGE}`,
                      }}
                    />
                  </Box>
                )}
              </Sekcija>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: { xs: '80vh', lg: 0 },
                borderLeft: { lg: '1px solid #eee' },
                backgroundColor: '#525659',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  backgroundColor: colors.WHITE,
                  borderBottom: '1px solid #eee',
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>
                  {t('projectReports.preview')}
                </Typography>
                {osvezava && (
                  <CircularProgress size={14} sx={{ color: colors.ORANGE }} />
                )}
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={jezik}
                  onChange={(_, vrednost) => vrednost && setJezik(vrednost)}
                >
                  <ToggleButton value="SRB">SRB</ToggleButton>
                  <ToggleButton value="ENG">ENG</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {pregledUrl ? (
                <iframe
                  title={t('projectReports.preview')}
                  src={`${pregledUrl}#toolbar=0&navpanes=0&view=FitH`}
                  style={{ flex: 1, width: '100%', border: 'none' }}
                />
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress sx={{ color: colors.WHITE }} />
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <OButton
          text={t('close')}
          startIcon={icons.DIALOG_CLOSE}
          onClick={onClose}
          disabled={cuva}
          style={{
            color: colors.ORANGE,
            backgroundColor: colors.WHITE,
            border: `1px solid ${colors.ORANGE}`,
          }}
        />
        <OButton
          text={t('buttons.save')}
          startIcon={<CheckIcon htmlColor={colors.WHITE} />}
          onClick={sacuvaj}
          isLoading={cuva}
          disabled={cuva || !model}
          style={{
            color: colors.WHITE,
            backgroundColor: colors.ORANGE,
            border: `1px solid ${colors.ORANGE}`,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default IzmenaIzvestajaProjektaDialog;
